import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { applyBranding, setBrandLogo } from "../lib/branding";

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  axiosInstance.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    setBrandLogo(null);
    if (user?.role !== "sub_broker") { applyBranding(); return; }
    axiosInstance.get("/associates/my-branding").then((r) => {
      applyBranding(r.data);
      if (r.data.has_logo) axiosInstance.get("/associates/my-branding/logo", { responseType: "blob" })
        .then((l) => setBrandLogo(URL.createObjectURL(l.data))).catch(() => {});
    }).catch(() => applyBranding());
  }, [user?.id, user?.role]); // eslint-disable-line

  const login = async (loginId, password) => {
    const response = await axiosInstance.post("/auth/login", { login_id: loginId, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const completePinLogin = async (pinToken, pin) => {
    const response = await axiosInstance.post("/auth/verify-pin", { pin_token: pinToken, pin });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    try {
      const { data } = await axiosInstance.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  const register = async (userData) => {
    const response = await axiosInstance.post("/auth/register", userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("impersonator_token");
    setToken(null);
    setUser(null);
  };

  // ─────────── Platform-owner impersonation ───────────
  // Lets the platform_owner step into any tenant's owner account
  // without typing a password. The original platform_owner token is
  // stashed in localStorage so we can swap back instantly.
  const impersonateTenant = async (tenantId) => {
    const { data } = await axiosInstance.post(`/platform/tenants/${tenantId}/impersonate`);
    const prevToken = localStorage.getItem("token");
    if (prevToken) localStorage.setItem("impersonator_token", prevToken);
    localStorage.setItem("token", data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const exitImpersonation = async () => {
    const { data } = await axiosInstance.post("/auth/exit-impersonation");
    localStorage.removeItem("impersonator_token");
    localStorage.setItem("token", data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  // Used by the public Start-Trial wizard to drop the freshly
  // provisioned tenant owner straight into the app — no separate
  // login step required.
  const setUserAndTokenAfterOnboarding = (newToken, newUser) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(newUser);
  };

  const value = {
    user,
    token,
    loading,
    login,
    completePinLogin,
    refreshUser,
    register,
    logout,
    impersonateTenant,
    exitImpersonation,
    setUserAndTokenAfterOnboarding,
    api: axiosInstance,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === "super_admin" || user?.role === "platform_owner",
    isBroker: user?.role === "broker" || user?.role === "super_admin" || user?.role === "platform_owner",
    isSubBroker: user?.role === "sub_broker",
    isClient: user?.role === "client",
    isImpersonating: !!user?.is_impersonating,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
